import { z } from "zod";

import { ApiFetchStatus } from "@tdp/common/constants";
import { ILogger } from "@tdp/common/telemetry";
import { parseApiResponse } from "@tdp/core/util/parseApiResponse";

import { AdaptiveCardItemSchema, IAdaptiveCardItem } from "../abstractions/IAdaptiveCards";
import { AdaptiveCardsDeleteResult, AdaptiveCardsFetchResult, IAdaptiveCardsReader } from "../abstractions/IAdaptiveCardsReader";
import { AdaptiveCardsCreateResult, AdaptiveCardsSendMeResult, IAdaptiveCardsWriter } from "../abstractions/IAdaptiveCardsWriter";

export interface TdpAdaptiveCardServiceDeps {
  logger: ILogger;
  apiFetch: typeof fetch;
}


export class TdpAdaptiveCardService implements IAdaptiveCardsReader, IAdaptiveCardsWriter {
  private logger: ILogger;
  private apiFetch: typeof fetch;

  constructor({ logger, apiFetch }: TdpAdaptiveCardServiceDeps) {
    this.apiFetch = apiFetch;
    this.logger = logger;
  }

  async createAdaptiveCard(payload: IAdaptiveCardItem): Promise<AdaptiveCardsCreateResult> {
    const response = await this.apiFetch('/cards', {
      method: "POST",
      body: JSON.stringify(payload),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    const parsedResponse = await parseApiResponse({
      response: response,
      errorMessage: "Error creating adaptive card",
      logger: this.logger,
      zodType: AdaptiveCardItemSchema,
    });
    if (parsedResponse.type === ApiFetchStatus.SUCCESS && parsedResponse.data) {
      return {
        type: ApiFetchStatus.SUCCESS,
        data: parsedResponse.data,
      }
    } else {
      return {
        type: ApiFetchStatus.ERROR,
        errorType: parsedResponse.errorType,
        errorMessage: parsedResponse.errorMessage,
      }
    }
  }

  async readAdaptiveCards(): Promise<AdaptiveCardsFetchResult> {
    const response = await this.apiFetch(`/cards/v2?pageNumber=1`);
    const parsedResponse = await parseApiResponse({
      response: response,
      errorMessage: "Error getting apps count",
      logger: this.logger,
      zodType: z.array(AdaptiveCardItemSchema),
    });
    if (parsedResponse.type === ApiFetchStatus.SUCCESS) {
      return {
        type: ApiFetchStatus.SUCCESS,
        data: parsedResponse.data || [],
      }
    } else {
      return {
        type: ApiFetchStatus.ERROR,
        errorType: parsedResponse.errorType,
        errorMessage: parsedResponse.errorMessage,
      }
    }
  }

  async updateAdaptiveCard(payload: IAdaptiveCardItem): Promise<AdaptiveCardsCreateResult> {
    const response = await this.apiFetch(`/cards/${payload.id}`, {
      method: "POST",
      body: JSON.stringify(payload),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    const parsedResponse = await parseApiResponse({
      response: response,
      errorMessage: "Error updating adaptive card",
      logger: this.logger,
      zodType: AdaptiveCardItemSchema,
    });
    if (parsedResponse.type === ApiFetchStatus.SUCCESS && parsedResponse.data) {
      return {
        type: ApiFetchStatus.SUCCESS,
        data: parsedResponse.data,
      }
    } else {
      return {
        type: ApiFetchStatus.ERROR,
        errorType: parsedResponse.errorType,
        errorMessage: parsedResponse.errorMessage,
      }
    }
  }

  async deleteAdaptiveCard(id: string): Promise<AdaptiveCardsDeleteResult> {
    const response = await this.apiFetch(`/cards/${id}`, {
      method: "DELETE",
    });
    const parsedResponse = await parseApiResponse({
      response: response,
      errorMessage: "Error deleting adaptive card",
      logger: this.logger,
      zodType: z.boolean(),
    });
    if (parsedResponse.type === ApiFetchStatus.SUCCESS) {
      return {
        type: ApiFetchStatus.SUCCESS,
      }
    } else {
      return {
        type: ApiFetchStatus.ERROR,
        errorType: parsedResponse.errorType,
        errorMessage: parsedResponse.errorMessage,
      }
    }
  }


  async sendMe(payload: string): Promise<AdaptiveCardsSendMeResult> {
    const response = await this.apiFetch(`/card/adaptive`, {
      method: "POST",
      body: JSON.stringify({
        jsonContent: payload
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    const parsedResponse = await parseApiResponse({
      response: response,
      errorMessage: "Error sending adaptive card",
      logger: this.logger,
      zodType: z.boolean(),
    });
    if (parsedResponse.type === ApiFetchStatus.SUCCESS && parsedResponse.data) {
      return {
        type: ApiFetchStatus.SUCCESS,
      }
    } else {
      return {
        type: ApiFetchStatus.ERROR,
        errorType: parsedResponse.errorType,
        errorMessage: parsedResponse.errorMessage,
      }
    }
  }

}
